<template>
    <v-app class="esign-shell">
      <v-main class="esign-main">
        <core-drawer @logout="logout()">
            <v-btn
              class="esign-icon-btn help-button"
              text
              icon
              to="/help"
              aria-label="Panduan pengguna"
              title="Panduan pengguna"
            >
              <v-icon>mdi-help-circle-outline</v-icon>
            </v-btn>
            <v-menu
              offset-y
              bottom
              left
              attach=".esign-navbar"
              @input="val => val && fetchLatestNotifications()"
              :close-on-content-click="false"
            >
              <!-- ACTIVATOR (ICON BEL) -->
              <template v-slot:activator="{ on, attrs }">
                <v-badge
                    :content="unreadCount"
                    :value="unreadCount > 0"
                    color="red"
                    overlap
                    offset-x="21"
                    offset-y="21"
                    bordered
                >
                  <v-btn
                    class="esign-icon-btn bell-button"
                    text
                    icon
                    aria-label="Notifikasi"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon class="bell-icon">mdi-bell-outline</v-icon>
                  </v-btn>
                </v-badge>
              </template>
              <v-card
                  width="420"
                  max-width="calc(100vw - 32px)"
                  class="notification-card"
                  elevation="4"
              >
                <div class="notification-header">
                  <span class="notification-header__title">Notifikasi</span>
                  <span v-if="unreadCount > 0" class="notification-header__count">{{ unreadCount }} belum dibaca</span>
                </div>
                <v-list dense class="notification-list">
                  <v-list-item v-if="loadingNotif" dense class="loading-item">
                    <v-list-item-content>
                      <v-list-item-title class="loading-text">Loading...</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <div v-if="!loadingNotif && notifications.length === 0" class="notification-empty">
                    <v-icon color="grey lighten-1" size="32">mdi-bell-check-outline</v-icon>
                    <span>Belum ada notifikasi</span>
                  </div>

                  <v-list-item
                      dense
                      v-for="notif in notifications"
                      :key="notif.id"
                      class="notification-item"
                      :class="{
                        'unread': !notif.is_read,
                        'success-type': notif.type === 'TTD_SUCCESS',
                        'failed-type': notif.type === 'TTD_FAILED',
                        'process-type': notif.type === 'TTD_PROCESSING'
                      }"
                      @click="markAsRead(notif.id)"
                  >
                    <v-list-item-avatar size="32">
                      <v-icon
                        size="20"
                        :color="(notifConfig[notif.type] && notifConfig[notif.type].color) || 'info'"
                      >
                        {{
                          (notifConfig[notif.type] && notifConfig[notif.type].icon) || 'mdi-sync-circle'
                        }}
                      </v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-subtitle
                          class="notif-title"
                          :class="{ 'unread-title': !notif.is_read }"
                      >
                        {{ notif.message }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle class="notif-time">
                        {{ formatDate(notif.created_at) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <v-divider class="notification-divider"></v-divider>

                  <v-list-item @click="$router.push('/notification')" dense class="view-all-item">
                    <v-list-item-content class="text-center">
                      <v-btn
                          text
                          small
                          color="primary"
                          class="text-caption view-all-btn"
                      >
                        Lihat semua
                      </v-btn>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
        </core-drawer>

        <div class="esign-content">
          <div class="esign-pagehead">
            <nav class="esign-breadcrumb" aria-label="Breadcrumb">
              <span>e-Sign</span>
              <!-- v-for on a real element: valid in Vue 2 and accepted by Vue 3 tooling -->
              <span
                v-for="(crumb, index) in breadcrumbs"
                :key="'crumb-' + index"
                class="esign-breadcrumb__item"
              >
                <v-icon small class="esign-breadcrumb__sep">mdi-chevron-right</v-icon>
                <span>{{ crumb }}</span>
              </span>
            </nav>
            <h1 class="esign-page-title">{{ pageTitle }}</h1>
          </div>
          <core-view />
        </div>

        <core-footer />
      </v-main>
    </v-app>
  </template>

  <script>
  import { getUnreadNotificationCount, getLatestNotifications, markAsReadNotification } from "@/services/NotificationService";
  import { connectNotificationSSE, closeNotificationSSE } from "@/services/NotificationSSE";

  export default {
    components: {
      CoreDrawer: () => import("@/components/core/Drawer"),
      CoreAppBar: () => import("@/components/core/AppBar"),
      CoreView: () => import("@/components/core/View"),
      CoreFooter: () => import("@/components/core/Footer"),
    },
    data() {
      return {
        unreadCount: 0,
        notifications: [],
        loadingNotif: false,
        tokenType: null,
        logoutTimer: null,
        currentUser: JSON.parse(localStorage.getItem("user") || "null"),
        currentPeran: localStorage.getItem("currentPeran"),
        notifConfig: {
          TTD_SUCCESS: {
            color: 'success',
            icon: 'mdi-check-circle'
          },
          TTD_PROCESSING: {
            color: 'info',
            icon: 'mdi-sync-circle'
          },
          TTD_FAILED: {
            color: 'error',
            icon: 'mdi-close-circle'
          }
        }
      }
    },
    computed: {
      menuList() {
        if (!this.currentUser || !this.currentUser.list_menu) return [];
        return this.currentUser.list_menu[this.currentPeran] || [];
      },
      activeMenu() {
        // Longest menu route that matches the current path
        const path = this.$route.path;
        return this.menuList
          .filter(m => m.route && m.route !== 'none' && (path === m.route || path.startsWith(m.route + '/')))
          .sort((a, b) => b.route.length - a.route.length)[0] || null;
      },
      pageTitle() {
        const name = this.$route.name;
        if (name === 'LOGIN_HOME') return 'Profil Pengguna';
        return name || 'e-Sign';
      },
      breadcrumbs() {
        const crumbs = [];
        const menu = this.activeMenu;
        if (menu && menu.id_parent) {
          const parent = this.menuList.find(m => String(m.id) === String(menu.id_parent));
          if (parent) crumbs.push(this.humanize(parent.nama));
        }
        if (this.roleName) crumbs.unshift(this.roleName);
        return crumbs;
      },
      roleName() {
        if (!this.currentUser || !this.currentUser.list_peran) return '';
        const peran = this.currentUser.list_peran.find(p => p.kode_peran === this.currentPeran);
        return peran ? peran.nama : '';
      }
    },
    watch: {
      $route() {
        this.currentPeran = localStorage.getItem("currentPeran");
      }
    },
    created() {
    },
    mounted(){
      this.tokenType = localStorage.getItem("token-type");

      this.fetchUnreadCount();

      connectNotificationSSE(
          this.onIncomingNotification,
          this.onSSEError
      );

      this.resetLogoutTimer();
      // Add listeners for user activity
      document.addEventListener('mousemove', this.resetLogoutTimer);
      document.addEventListener('keypress', this.resetLogoutTimer);
    },
    beforeDestroy() {
      closeNotificationSSE();

      // Remove listeners for user activity
      document.removeEventListener('mousemove', this.resetLogoutTimer);
      document.removeEventListener('keypress', this.resetLogoutTimer);
    },
    methods: {
      humanize(camelCase) {
        const result = String(camelCase).replace(/([a-z])([A-Z])/g, '$1 $2');
        return result.charAt(0).toUpperCase() + result.slice(1);
      },
      onIncomingNotification(notif) {
        this.notifications.unshift(notif);

        this.unreadCount++;

        this.$store.dispatch("setSnackbar", {
          snackbarColor: "info",
          snackbarText: notif.message,
        });
      },
      onSSEError(err) {
        console.warn("SSE disconnected", err);
      },
      async markAsRead(notificationId) {
        const idx = this.notifications.findIndex(n => n.ID === notificationId)
        if (idx === -1) return

        const notif = this.notifications[idx]
        if (notif.is_read) return

        const prevIsRead = notif.is_read
        const prevUnread = this.unreadCount

        try {
          await markAsReadNotification(notificationId);

          const updated = { ...notif, is_read: true }
          this.notifications.splice(idx, 1, updated)
          this.unreadCount = Math.max(0, this.unreadCount - 1)
        } catch (err) {
          notif.is_read = prevIsRead
          this.unreadCount = prevUnread

          console.error("Gagal mark as read", err)
        }
      },
      formatDate(dateStr) {
        const d = new Date(dateStr);
        return d.toLocaleString("id-ID", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        });
      },
      async fetchLatestNotifications() {
        this.loadingNotif = true;
        try {
          const res = await getLatestNotifications();
          this.notifications = res.data;
        } catch (err) {
          console.error("Gagal ambil notifikasi", err);
        } finally {
          this.loadingNotif = false;
        }
      },
      async fetchUnreadCount() {
        try {
          const res = await getUnreadNotificationCount();
          this.unreadCount = res.data.count;
        } catch (err) {
          console.error("Gagal ambil unread notif", err);
        }
      },
      logout() {
        document.removeEventListener('mousemove', this.resetLogoutTimer);
        document.removeEventListener('keypress', this.resetLogoutTimer);
        this.$store.dispatch("setSnackbar", {
          snackbarColor: "primary",
          snackbarText: "Logout Berhasil",
        });
        this.$store.dispatch("logout");
      },
      resetLogoutTimer() {
        // Clear the existing timer
        if (this.logoutTimer) clearTimeout(this.logoutTimer);
        // Set a new timer
        if (this.tokenType === 'Keycloak') {
          this.logoutTimer = setTimeout(this.logout, 1800000); // 30 minutes
        } else {
          this.logoutTimer = setTimeout(this.logout, 3600000); // 1 hour
        }
      },
    }
  };
  </script>
  <style>
    @import url("../css/style.css");

    .v-list {
      padding: 0;
    }

    /* ---------- App shell ---------- */
    .esign-shell.v-application {
      background: var(--esign-bg) !important;
    }

    .esign-main > .v-main__wrap {
      display: flex;
      flex-direction: column;
      min-height: 100vh;
    }

    .esign-content {
      flex: 1 0 auto;
      width: 100%;
      max-width: none;
      margin: 0 auto;
      padding: 32px 24px 40px;
    }

    /* the page heading sets the space above the first card */
    .esign-content > #core-view > .container {
      padding-top: 0;
      padding-left: 0;
      padding-right: 0;
    }

    /* ---------- Page heading ---------- */
    .esign-pagehead {
      margin-bottom: 16px;
    }

    .esign-breadcrumb {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 2px;
      font-size: 12px;
      font-weight: 500;
      line-height: 1.4;
      color: var(--esign-text-subtle);
    }

    .esign-breadcrumb .esign-breadcrumb__sep.v-icon {
      color: var(--esign-border-strong);
    }

    .v-application .esign-page-title {
      margin: 2px 0 0;
      font-size: 24px !important;
      font-weight: 800 !important;
      line-height: 1.3 !important;
      color: var(--esign-text);
      overflow-wrap: anywhere;
    }

    /* Round icon buttons (notification bell) */
    .v-application .esign-icon-btn.v-btn {
      width: 40px;
      height: 40px;
      border: 1px solid var(--esign-border);
      background-color: var(--esign-surface);
      color: var(--esign-text-muted) !important;
      transition: background-color var(--esign-fast) var(--esign-ease),
        border-color var(--esign-fast) var(--esign-ease),
        color var(--esign-fast) var(--esign-ease);
    }

    @media (max-width: 600px) {
      .esign-content {
        padding: 20px max(12px, env(safe-area-inset-right, 0px)) calc(32px + env(safe-area-inset-bottom, 0px)) max(12px, env(safe-area-inset-left, 0px));
      }

      .esign-pagehead {
        margin-bottom: 12px;
      }

      .v-application .esign-page-title {
        font-size: 19px !important;
      }
    }

    /* ---------- Notifications ---------- */
    .bell-button {
      transition: background-color 0.3s ease, transform 0.3s ease;
    }

    .bell-button:hover .bell-icon {
      animation: bell-shake 0.5s ease-in-out;
    }

    .notification-card {
      border-radius: var(--esign-radius-md) !important;
      box-shadow: var(--esign-shadow-md) !important;
      overflow: hidden;
    }

    .notification-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 16px;
      border-bottom: 1px solid var(--esign-border);
    }

    .notification-header__title {
      font-size: 15px;
      font-weight: 700;
      color: var(--esign-text);
    }

    .notification-header__count {
      padding: 2px 10px;
      border-radius: 999px;
      background: var(--esign-primary-subtle);
      color: var(--esign-primary-strong);
      font-size: 12px;
      font-weight: 600;
    }

    .notification-empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      padding: 28px 16px;
      color: var(--esign-text-subtle);
      font-size: 14px;
    }

    .notification-list {
      padding: 0;
      max-height: 400px;
      overflow-y: auto;
    }

    .loading-item {
      justify-content: center;
      padding: 16px;
    }

    .loading-text {
      font-size: 14px;
      color: var(--esign-text-muted);
      text-align: center;
    }

    .notification-item {
      padding: 12px 16px;
      border-bottom: 1px solid var(--esign-border);
      border-left: 3px solid transparent;
      transition: background-color 0.2s ease;
      cursor: pointer;
      gap: 12px;
    }

    .notification-item:hover {
      background-color: var(--esign-surface-muted);
    }

    .notification-item.unread {
      background-color: var(--esign-primary-soft);
      border-left-color: var(--esign-primary);
    }

    .notification-item.unread:hover {
      background-color: var(--esign-primary-subtle);
    }

    .notif-title {
      font-size: 14px;
      line-height: 1.4;
      color: var(--esign-text) !important;
      margin-bottom: 4px;
      text-align: left;
      white-space: normal !important;
    }

    .unread-title {
      font-weight: 600;
    }

    .notif-time {
      font-size: 12px;
      color: var(--esign-text-subtle) !important;
      margin-top: 0;
    }

    .notification-divider {
      margin: 0;
      border-color: var(--esign-border) !important;
    }

    .view-all-item {
      justify-content: center;
    }

    .view-all-btn {
      font-weight: 600;
      text-transform: none;
    }

    /* Animasi shake untuk bell */
    @keyframes bell-shake {
      0%, 100% { transform: rotate(0deg); }
      25% { transform: rotate(15deg); }
      50% { transform: rotate(0deg); }
      75% { transform: rotate(-15deg); }
      100% { transform: rotate(0deg); }
    }
  </style>
